import { Box, Divider, Icon, Tab, Tabs } from "@mui/material";
import {
    Suspense,
    SyntheticEvent,
    useContext,
    useEffect,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "../../../components/exports";
import { DialogContext } from "../../../contexts/DialogueContext";
import { fetchProjectDetail } from "../../../slices/ProjectDetailSlice";
import { setProjectDetailIndex } from "../../../slices/SharedSlice";
import { RootState } from "../../../slices/Store";
import ProjectDetailAddress from "../Shared/ProjectDetailAddress";
import ProjectDetailConstructor from "../Shared/ProjectDetailConstructor";
import ProjectDetailExtraInfo from "../Shared/ProjectDetailExtraInfo";
import ProjectDetailDesktopSelectFolder from "../Shared/ProjectDetailFolders";
import ProjectDetailInfo from "../Shared/ProjectDetailInfo";
import ProjectDetailInvalidDataAndVisibility from "../Shared/ProjectDetailInvalidDataAndVisibility";
import ProjectDetailMap from "../Shared/ProjectDetailMap";
import ProjectDetailNotes from "../Shared/ProjectDetailNotes";
import ProjectDetailSales from "../Shared/ProjectDetailSales";
import ProjectDetailViewConstructor from "../Shared/ProjectDetailViewConstructor";
import ProjectDetailDesktopHeader from "./ProjectDetailDesktopHeader";
import ProjectDetailDesktopPhotos from "./ProjectDetailDesktopPhotos";
import ProjectDetailDesktopSms from "./ProjectDetailDesktopSms";

// updated
export default function ProjectDetailDesktopDialog(props: {
    selectedFollowUpId?: number;
    openFollowUps?: boolean;
    openSales?: boolean;
    openNotes?: boolean;
}) {
    let tabIndex = 0;

    if (props.openSales) {
        tabIndex = 1;
    }

    if (props.openNotes) {
        tabIndex = 2;
    }

    useEffect(() => {
        function updateSize() {
            setWidth(window.innerWidth);
        }
        window.addEventListener("resize", updateSize);
        updateSize();
        return () => window.removeEventListener("resize", updateSize);
    }, []);

    function downHandler({ key }) {
        if (key === "ArrowLeft") {
            loadNextProject();
        } else if (key === "ArrowRight") {
            loadPrevProject();
        }
    }

    useEffect(() => {
        window.addEventListener("keydown", downHandler);
        return () => {
            window.removeEventListener("keydown", downHandler);
        };
    }, []);

    const [tab, setTab] = useState(tabIndex);
    const [width, setWidth] = useState(window.innerWidth);
    const { closeDialog } = useContext(DialogContext);

    const dispatch = useDispatch();

    const projectDetailState = useSelector(
        (state: RootState) => state.projectDetail
    );
    const sharedState = useSelector((state: RootState) => state.shared);
    const projectState = useSelector((state: RootState) => state.projects);
    const projectDetail = projectDetailState.projectDetail;

    const handleTabChange = (event: SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    const loadPrevProject = () => {
        if (sharedState.projectDetailIndex === -1) {
            return;
        }

        const prevProject =
            projectState.projects[sharedState.projectDetailIndex - 1];

        if (!prevProject) {
            return;
        }

        fetchProjectDetail(
            projectDetailState,
            prevProject.hashId,
            null,
            dispatch,
            (result) => {
                if (!result) {
                    closeDialog();
                }
            }
        );
        dispatch(setProjectDetailIndex(sharedState.projectDetailIndex - 1));
    };

    const loadNextProject = () => {
        if (sharedState.projectDetailIndex === -1) {
            return;
        }

        const nextProject =
            projectState.projects[sharedState.projectDetailIndex + 1];

        if (!nextProject) {
            return;
        }

        fetchProjectDetail(
            projectDetailState,
            nextProject.hashId,
            null,
            dispatch,
            (result) => {
                if (!result) {
                    closeDialog();
                }
            }
        );
        dispatch(setProjectDetailIndex(sharedState.projectDetailIndex + 1));
    };

    const offset = (width - 1100) / 2;

    const hasEnoughWidth = width >= 1150;

    return (
        <Box sx={{ position: "relative" }}>
            <Box sx={{ bgcolor: "background.default", display: "flex" }}>
                <Box sx={{ width: "432px" }}>
                    <ProjectDetailDesktopHeader />
                    <ProjectDetailDesktopPhotos />
                    <Box sx={{ px: 2 }}>
                        <ProjectDetailInvalidDataAndVisibility />
                    </Box>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box sx={{ width: "460px" }}>
                    <Tabs
                        sx={{
                            height: 50,
                            bgcolor: "background.paper",
                        }}
                        value={tab}
                        onChange={handleTabChange}
                        centered
                    >
                        <Tab
                            sx={{ minWidth: "auto" }}
                            label="اطلاعات پروژه"
                            value={0}
                        />
                        <Tab
                            sx={{ minWidth: "auto" }}
                            label="فرایند فروش"
                            value={1}
                        />
                        <Tab
                            sx={{ minWidth: "auto" }}
                            label="یادداشت ها"
                            value={2}
                        />
                        <Tab
                            sx={{ minWidth: "auto" }}
                            label="ارسال پیامک"
                            value={3}
                        />
                        <Tab sx={{ minWidth: "auto" }} label="نقشه" value={4} />
                    </Tabs>

                    <Divider />

                    <Box sx={{ p: 2, pb: 0 }}>
                        {tab === 0 && (
                            <>
                                <ProjectDetailExtraInfo />
                                <ProjectDetailViewConstructor />
                                <ProjectDetailConstructor />
                                <ProjectDetailAddress />
                                <ProjectDetailInfo />
                                <ProjectDetailDesktopSelectFolder />
                            </>
                        )}

                        {tab === 1 && (
                            <>
                                <ProjectDetailSales />
                            </>
                        )}

                        {tab === 2 && (
                            <>
                                <ProjectDetailNotes />
                            </>
                        )}

                        {tab === 3 && (
                            <>
                                <ProjectDetailDesktopSms
                                    projects={[projectDetail]}
                                />
                            </>
                        )}

                        {tab === 4 && (
                            <>
                                <Suspense fallback={<Spinner />}>
                                    <ProjectDetailMap />
                                </Suspense>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {sharedState.projectDetailIndex !== -1 && hasEnoughWidth && (
                <>
                    {sharedState.projectDetailIndex !== 0 && (
                        <Box
                            sx={{
                                ":hover": { opacity: 1 },
                                cursor: "pointer",
                                transition: "opacity 0.5s",
                                opacity: 0.5,
                                position: "fixed",
                                top: "47%",
                                left: offset,
                                bgcolor: "background.paper",
                                width: 65,
                                height: 65,
                                borderRadius: "37.5px",
                                lineHeight: "65px",
                                textAlign: "center",
                            }}
                            onClick={loadPrevProject}
                        >
                            <Icon
                                color="primary"
                                sx={{
                                    fontSize: 30,
                                    lineHeight: "65px",
                                    height: "65px",
                                }}
                            >
                                arrow_forward
                            </Icon>
                        </Box>
                    )}

                    {sharedState.projectDetailIndex !==
                        projectState.projects.length - 1 && (
                        <Box
                            sx={{
                                ":hover": { opacity: 1 },
                                cursor: "pointer",
                                transition: "opacity 0.5s",
                                opacity: 0.5,
                                position: "fixed",
                                top: "47%",
                                right: offset,
                                bgcolor: "background.paper",
                                width: 65,
                                height: 65,
                                borderRadius: "37.5px",
                                lineHeight: "65px",
                                textAlign: "center",
                            }}
                            onClick={loadNextProject}
                        >
                            <Icon
                                color="primary"
                                sx={{
                                    fontSize: 30,
                                    lineHeight: "65px",
                                    height: "65px",
                                }}
                            >
                                arrow_back
                            </Icon>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
